'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

type RecordingState = 'idle' | 'recording' | 'paused';
type ConvertState = 'idle' | 'converting' | 'done' | 'error';

interface Recording {
  url: string;
  blob: Blob;
  duration: number;
  timestamp: string;
  mimeType: string;
}

const BAR_COUNT = 28;

export default function VoiceRecorderTool() {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);

  // Per-recording convert state
  const [convertStates, setConvertStates] = useState<Record<number, ConvertState>>({});
  const [convertProgress, setConvertProgress] = useState<Record<number, number>>({});
  const [convertUrls, setConvertUrls] = useState<Record<number, string>>({});
  const [convertError, setConvertError] = useState<Record<number, string>>({});

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedElapsedRef = useRef<number>(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barsRef = useRef<number[]>(Array(BAR_COUNT).fill(0));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const supported = !!(
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia &&
        window.MediaRecorder
      );
      setIsSupported(supported);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const drawBars = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const barW = Math.floor(W / BAR_COUNT) - 2;
    barsRef.current.forEach((h, i) => {
      const x = i * (barW + 2);
      const barH = Math.max(4, h * H);
      const y = (H - barH) / 2;
      ctx.fillStyle = h > 0.5 ? '#F59E0B' : h > 0.2 ? '#FCD34D' : '#E5E7EB';
      ctx.beginPath();
      ctx.roundRect(x, y, barW, barH, 3);
      ctx.fill();
    });
  }, []);

  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;
    const data = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(data);
    const avg = data.reduce((a, b) => a + b, 0) / data.length;
    const level = avg / 255;
    setAudioLevel(level);

    // Shift bars left and push new value
    const bars = barsRef.current;
    for (let i = 0; i < BAR_COUNT - 1; i++) {
      bars[i] = bars[i + 1];
    }
    bars[BAR_COUNT - 1] = level * (0.6 + Math.random() * 0.4);
    drawBars();

    animFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, [drawBars]);

  // Draw idle bars once
  useEffect(() => {
    barsRef.current = Array(BAR_COUNT).fill(0);
    drawBars();
  }, [drawBars]);

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm' :'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const duration =
          pausedElapsedRef.current +
          Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordings(prev => [
          {
            url,
            blob,
            duration,
            timestamp: new Date().toLocaleTimeString(),
            mimeType,
          },
          ...prev,
        ]);
        setElapsed(0);
        pausedElapsedRef.current = 0;
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        setAudioLevel(0);
        barsRef.current = Array(BAR_COUNT).fill(0);
        drawBars();
        stream.getTracks().forEach(t => t.stop());
      };

      recorder.start(100);
      startTimeRef.current = Date.now();
      setRecordingState('recording');

      timerRef.current = setInterval(() => {
        setElapsed(
          pausedElapsedRef.current +
            Math.floor((Date.now() - startTimeRef.current) / 1000)
        );
      }, 500);

      updateAudioLevel();
    } catch (err: unknown) {
      const msg =
        err instanceof Error && err.name === 'NotAllowedError' ?'Microphone access was blocked. Please click the 🔒 lock icon in your browser\'s address bar and allow microphone access, then try again.'
          : err instanceof Error && err.name === 'NotFoundError' ?'No microphone found. Please connect a microphone and try again.' :'Could not access microphone. Please check your browser settings.';
      setError(msg);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      pausedElapsedRef.current += Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      );
      if (timerRef.current) clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      setAudioLevel(0);
      setRecordingState('paused');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      startTimeRef.current = Date.now();
      setRecordingState('recording');
      timerRef.current = setInterval(() => {
        setElapsed(
          pausedElapsedRef.current +
            Math.floor((Date.now() - startTimeRef.current) / 1000)
        );
      }, 500);
      updateAudioLevel();
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
    setRecordingState('idle');
  };

  const handleDownload = (rec: Recording, index: number, format: 'webm' | 'wav') => {
    const ext = format === 'wav' ? 'wav' : rec.mimeType.includes('ogg') ? 'ogg' : 'webm';
    const a = document.createElement('a');
    a.href = rec.url;
    a.download = `recording_${index + 1}.${ext}`;
    a.click();
  };

  const handleConvertToMp3 = async (rec: Recording, index: number) => {
    setConvertStates(prev => ({ ...prev, [index]: 'converting' }));
    setConvertProgress(prev => ({ ...prev, [index]: 0 }));
    setConvertError(prev => ({ ...prev, [index]: '' }));

    try {
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

      const ffmpeg = new FFmpeg();

      ffmpeg.on('progress', ({ progress: p }) => {
        setConvertProgress(prev => ({ ...prev, [index]: Math.round(10 + p * 85) }));
      });

      setConvertProgress(prev => ({ ...prev, [index]: 5 }));

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setConvertProgress(prev => ({ ...prev, [index]: 10 }));

      const inputExt = rec.mimeType.includes('ogg') ? 'ogg' : 'webm';
      await ffmpeg.writeFile(`input.${inputExt}`, await fetchFile(rec.blob));

      await ffmpeg.exec([
        '-i', `input.${inputExt}`,
        '-codec:a', 'libmp3lame',
        '-b:a', '192k',
        'output.mp3',
      ]);

      setConvertProgress(prev => ({ ...prev, [index]: 97 }));

      const data = await ffmpeg.readFile('output.mp3');
      const mp3Blob = new Blob([data], { type: 'audio/mpeg' });
      const mp3Url = URL.createObjectURL(mp3Blob);

      setConvertUrls(prev => ({ ...prev, [index]: mp3Url }));
      setConvertStates(prev => ({ ...prev, [index]: 'done' }));
      setConvertProgress(prev => ({ ...prev, [index]: 100 }));
    } catch (err) {
      console.error(err);
      setConvertError(prev => ({
        ...prev,
        [index]: 'MP3 conversion failed. Please try again.',
      }));
      setConvertStates(prev => ({ ...prev, [index]: 'error' }));
    }
  };

  const handleDeleteRecording = (index: number) => {
    setRecordings(prev => prev.filter((_, i) => i !== index));
    setConvertStates(prev => {
      const n = { ...prev };
      delete n[index];
      return n;
    });
    setConvertUrls(prev => {
      const n = { ...prev };
      delete n[index];
      return n;
    });
  };

  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 text-center">
        <span className="text-5xl block mb-4">🎙️</span>
        <p className="font-bold text-yellow-800 mb-2 text-lg">Voice Recording Not Supported</p>
        <p className="text-yellow-700 text-sm">
          Your browser does not support the MediaRecorder API. Please use Chrome, Firefox, or Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Privacy note */}
      <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-2.5">
        <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
        <p className="text-green-700 text-sm font-medium">Your recording stays on your device. Nothing is uploaded to any server.</p>
      </div>

      {/* Mic permission error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div>
            <p className="text-red-700 text-sm font-semibold mb-0.5">Microphone Access Required</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Recorder card */}
      <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 text-center space-y-5">
        {/* Timer */}
        <div className="text-6xl sm:text-7xl font-mono font-bold text-gray-800 tabular-nums tracking-tight">
          {formatTime(elapsed)}
        </div>

        {/* Waveform canvas */}
        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            width={BAR_COUNT * 14}
            height={48}
            className="rounded-lg"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>

        {/* Status */}
        <p className="text-sm font-medium text-gray-500">
          {recordingState === 'idle' && 'Tap the button to start recording'}
          {recordingState === 'recording' && (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Recording…
            </span>
          )}
          {recordingState === 'paused' && '⏸ Paused — tap resume to continue'}
        </p>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          {recordingState === 'idle' && (
            <button
              onClick={startRecording}
              className="w-20 h-20 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 text-white flex items-center justify-center shadow-xl shadow-red-200 transition-all"
              title="Start recording"
              aria-label="Start recording"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
              </svg>
            </button>
          )}

          {(recordingState === 'recording' || recordingState === 'paused') && (
            <>
              {/* Pause / Resume */}
              <button
                onClick={recordingState === 'recording' ? pauseRecording : resumeRecording}
                className="w-14 h-14 rounded-full bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-700 flex items-center justify-center transition-all shadow-md"
                title={recordingState === 'recording' ? 'Pause' : 'Resume'}
                aria-label={recordingState === 'recording' ? 'Pause recording' : 'Resume recording'}
              >
                {recordingState === 'recording' ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16" rx="1.5" />
                    <rect x="14" y="4" width="4" height="16" rx="1.5" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Stop */}
              <button
                onClick={stopRecording}
                className="w-20 h-20 rounded-full bg-gray-800 hover:bg-gray-900 active:scale-95 text-white flex items-center justify-center shadow-xl transition-all"
                title="Stop recording"
                aria-label="Stop recording"
              >
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="2.5" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Level indicator */}
        {recordingState === 'recording' && (
          <div className="flex items-center justify-center gap-1.5">
            <span className="text-xs text-gray-400">Level</span>
            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-75"
                style={{
                  width: `${Math.round(audioLevel * 100)}%`,
                  backgroundColor: audioLevel > 0.7 ? '#EF4444' : audioLevel > 0.4 ? '#F59E0B' : '#22C55E',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Recordings list */}
      {recordings.length > 0 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-gray-700">
            Recordings ({recordings.length})
          </p>

          {recordings.map((rec, i) => {
            const displayIndex = recordings.length - i;
            const cState = convertStates[i] ?? 'idle';
            const cProgress = convertProgress[i] ?? 0;
            const cUrl = convertUrls[i];
            const cErr = convertError[i];

            return (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 space-y-4 shadow-sm"
              >
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">Recording {displayIndex}</p>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {rec.timestamp} · {formatTime(rec.duration)} · {formatBytes(rec.blob.size)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteRecording(i)}
                    className="text-gray-300 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                    aria-label="Delete recording"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Playback */}
                <audio src={rec.url} controls className="w-full h-10" />

                {/* Download WebM / WAV */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleDownload(rec, displayIndex, 'webm')}
                    className="py-2.5 bg-amber-500 hover:bg-amber-600 active:scale-95 text-white font-semibold rounded-xl transition-all text-sm"
                  >
                    ⬇ Download WebM
                  </button>
                  <button
                    onClick={() => handleDownload(rec, displayIndex, 'wav')}
                    className="py-2.5 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-800 font-semibold rounded-xl transition-all text-sm"
                  >
                    ⬇ Download WAV
                  </button>
                </div>

                {/* MP3 conversion */}
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  {cState === 'idle' && (
                    <button
                      onClick={() => handleConvertToMp3(rec, i)}
                      className="w-full py-2.5 bg-gray-800 hover:bg-gray-900 active:scale-95 text-white font-semibold rounded-xl transition-all text-sm flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
                      </svg>
                      Convert to MP3
                    </button>
                  )}

                  {cState === 'converting' && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Converting to MP3…</span>
                        <span>{cProgress}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all duration-300"
                          style={{ width: `${cProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 text-center">
                        {cProgress < 10 ? 'Loading FFmpeg engine…' : cProgress < 15 ? 'Loading FFmpeg core…' : 'Converting…'}
                      </p>
                    </div>
                  )}

                  {cState === 'done' && cUrl && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-green-700 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        MP3 ready
                      </p>
                      <audio src={cUrl} controls className="w-full h-10" />
                      <a
                        href={cUrl}
                        download={`recording_${displayIndex}.mp3`}
                        className="block w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all text-sm text-center"
                      >
                        ⬇ Download MP3
                      </a>
                    </div>
                  )}

                  {cState === 'error' && (
                    <div className="space-y-2">
                      <p className="text-xs text-red-600">{cErr}</p>
                      <button
                        onClick={() => handleConvertToMp3(rec, i)}
                        className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all text-sm"
                      >
                        Retry MP3 Conversion
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
